import { z } from 'zod';
import type { TrailerBodyStyle, TrailerHitch, TruckBodyStyle, TruckCoupling } from './fleet.schemas';
import { defineListQuery, inForceFilter, listFilter } from './list-query.schemas';
import { TRUCK_COMPLIANCE_STATUSES, TRUCK_STATUSES, type TruckComplianceStatus, type TruckStatus } from './truck.schemas';

/**
 * Which trailer a truck pulls, over time (gitaumoses4/chukua#390). A coupling is effective-dated: a truck has at most one trailer
 * and a trailer at most one truck at a time. A swap ends one coupling and starts another; a trip keeps the trailer it pulled.
 */

const instantSchema = z.iso.datetime({ offset: true });
const zoneCodeSchema = z.string().trim().min(1).max(64);

/**
 * `POST /couplings`. `coupledAt` defaults to now. When the truck already pulls a trailer, that one is dropped at `dropZoneCode`
 * (required then). A trailer coupled to another truck moves across: its old coupling ends with no drop place.
 */
export const coupleTrailerSchema = z
  .object({
    truckId: z.uuid(),
    trailerId: z.uuid(),
    coupledAt: instantSchema.optional(),
    dropZoneCode: zoneCodeSchema.optional(),
  })
  .strict();
export type CoupleTrailerInput = z.infer<typeof coupleTrailerSchema>;

/** `POST /couplings/:id/uncouple`: where the trailer is left, and when (default now). */
export const uncoupleTrailerSchema = z
  .object({
    dropZoneCode: zoneCodeSchema,
    uncoupledAt: instantSchema.optional(),
  })
  .strict();
export type UncoupleTrailerInput = z.infer<typeof uncoupleTrailerSchema>;

export interface TruckTrailerCoupling {
  id: string;
  truckId: string;
  truckRegistration: string;
  trailerId: string;
  trailerRegistration: string;
  coupledAt: string;
  /** Null while current. */
  uncoupledAt: string | null;
  /** Where the trailer was left; null while current, and when it moved straight onto another truck. */
  dropZoneCode: string | null;
  dropZoneName: string | null;
  coupledByName: string | null;
  uncoupledByName: string | null;
}

/** `GET /couplings`: a truck's or a trailer's coupling history; `current=true` is what is coupled now. */
export const couplingListQuerySchema = defineListQuery({
  sortFields: ['coupledAt'],
  defaultSort: { sort: 'coupledAt', order: 'desc' },
  filters: {
    truckId: listFilter(z.uuid()),
    trailerId: listFilter(z.uuid()),
    current: inForceFilter,
  },
});
export type CouplingListQuery = z.infer<typeof couplingListQuerySchema>;

/**
 * A unit the office can send out: a coupled rig, a truck on its own, or a parked trailer. The office thinks of a truck and the
 * trailer it pulls as one unit, named by the truck's registration (gitaumoses4/chukua#390).
 */
export const RIG_KINDS = ['coupled', 'truck_only', 'trailer_only'] as const;
export type RigKind = (typeof RIG_KINDS)[number];

/**
 * `GET /rigs`, Fleet → Units: search matches either registration; `compliance` is the worse of the two vehicles'. `truckId` /
 * `trailerId` find the unit a vehicle is part of. `status` and `classCode` are the truck's. A deactivated trailer is left out
 * unless `deactivated=true`, which lists only those.
 */
export const rigListQuerySchema = defineListQuery({
  sortFields: ['registration', 'coupledAt'],
  defaultSort: { sort: 'registration', order: 'asc' },
  filters: {
    kind: listFilter(z.enum(RIG_KINDS)),
    compliance: listFilter(z.enum(TRUCK_COMPLIANCE_STATUSES)),
    status: listFilter(z.enum(TRUCK_STATUSES)),
    classCode: listFilter(z.string()),
    truckId: listFilter(z.uuid()),
    trailerId: listFilter(z.uuid()),
    deactivated: inForceFilter,
  },
});
export type RigListQuery = z.infer<typeof rigListQuerySchema>;

export interface RigTruck {
  id: string;
  registration: string;
  classCode: string;
  className: string | null;
  bodyStyle: TruckBodyStyle | null;
  coupling: TruckCoupling | null;
  status: TruckStatus;
  color: string;
  compliance: TruckComplianceStatus;
}

export interface RigTrailer {
  id: string;
  registration: string;
  typeCode: string;
  typeName: string | null;
  bodyStyle: TrailerBodyStyle | null;
  hitch: TrailerHitch | null;
  color: string;
  compliance: TruckComplianceStatus;
  isActive: boolean;
}

export interface RigListItem {
  /** Stable across pages: the truck's id, or the parked trailer's. */
  key: string;
  kind: RigKind;
  truck: RigTruck | null;
  trailer: RigTrailer | null;
  /** The current coupling, on a coupled rig. */
  couplingId: string | null;
  coupledAt: string | null;
  /** Where a parked trailer was left, and when; null if it has never been coupled. */
  parkedAt: { zoneCode: string; zoneName: string | null; since: string } | null;
  compliance: TruckComplianceStatus;
}

/** One vehicle of a unit, as `GET /units/:registration` names it. */
export interface FleetUnitVehicle {
  id: string;
  registration: string;
}

/**
 * `GET /units/:registration` (a registration slug, `registrationSlug`): the unit a registration belongs to. A truck's is the
 * truck and its current trailer; a coupled trailer's is the truck pulling it, so its address leads to that truck's unit; a spare
 * trailer's has no truck. `matched` says which of the two the registration named.
 */
export interface FleetUnit {
  matched: 'truck' | 'trailer';
  truck: FleetUnitVehicle | null;
  trailer: FleetUnitVehicle | null;
  /** The current coupling, when there is one. */
  couplingId: string | null;
}
